import { v4 as uuidv4 } from 'uuid';
import { PoopReport, TimeFilter } from '../types';

export function generateId(): string {
  return uuidv4();
}

// Simple geohash implementation (no external dependency needed)
// Encodes lat/lng into a string that groups nearby locations
export function encodeGeohash(lat: number, lng: number, precision: number = 9): string {
  const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
  let idx = 0;
  let bit = 0;
  let evenBit = true;
  let geohash = '';
  let minLat = -90, maxLat = 90;
  let minLng = -180, maxLng = 180;

  while (geohash.length < precision) {
    if (evenBit) {
      const midLng = (minLng + maxLng) / 2;
      if (lng >= midLng) {
        idx = idx * 2 + 1;
        minLng = midLng;
      } else {
        idx = idx * 2;
        maxLng = midLng;
      }
    } else {
      const midLat = (minLat + maxLat) / 2;
      if (lat >= midLat) {
        idx = idx * 2 + 1;
        minLat = midLat;
      } else {
        idx = idx * 2;
        maxLat = midLat;
      }
    }
    evenBit = !evenBit;

    if (++bit === 5) {
      geohash += BASE32.charAt(idx);
      bit = 0;
      idx = 0;
    }
  }
  return geohash;
}

// Check if there's a pin within a certain distance (in meters)
export function findNearbyReport(
  lat: number, 
  lng: number, 
  reports: PoopReport[], 
  radiusMeters: number = 20
): PoopReport | null {
  for (const report of reports) {
    const distance = getDistanceMeters(lat, lng, report.lat, report.lng);
    if (distance <= radiusMeters) {
      return report;
    }
  }
  return null;
}

// Calculate distance between two points in meters (Haversine formula)
export function getDistanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function blurLocation(lat: number, lng: number, meters: number): { lat: number; lng: number } {
  const earthRadius = 6371000;
  const angle = Math.random() * 2 * Math.PI;
  const distance = Math.random() * meters;
  
  const deltaLat = (distance * Math.cos(angle)) / earthRadius;
  const deltaLng = (distance * Math.sin(angle)) / (earthRadius * Math.cos(lat * Math.PI / 180));
  
  return {
    lat: lat + (deltaLat * 180 / Math.PI),
    lng: lng + (deltaLng * 180 / Math.PI)
  };
}

export function calculateDecayOpacity(createdAt: string, expiryDays: number): number {
  const age = Date.now() - new Date(createdAt).getTime();
  const maxAge = expiryDays * 24 * 60 * 60 * 1000;
  const ratio = age / maxAge;
  return Math.max(0.3, 1 - ratio * 0.7);
}

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function filterByTimeRange(reports: PoopReport[], filter: TimeFilter): PoopReport[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  let startDate: Date;
  let endDate: Date = now;
  
  switch (filter) {
    case 'this-week':
      // Start from Monday of current week
      const dayOfWeek = now.getDay();
      const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(currentYear, currentMonth, now.getDate() - daysFromMonday);
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'this-month':
      startDate = new Date(currentYear, currentMonth, 1);
      break;
    case 'last-month':
      startDate = new Date(currentYear, currentMonth - 1, 1);
      endDate = new Date(currentYear, currentMonth, 0, 23, 59, 59);
      break;
    case 'ytd':
      startDate = new Date(currentYear, 0, 1);
      break;
    case 'last-year':
      startDate = new Date(currentYear - 1, 0, 1);
      endDate = new Date(currentYear - 1, 11, 31, 23, 59, 59);
      break;
    default:
      startDate = new Date(0);
  }
  
  return reports.filter(r => {
    const reportDate = new Date(r.createdAt);
    return reportDate >= startDate && reportDate <= endDate;
  });
}
